import * as types from '../constants/ActionTypes';
import { assocPath, keys, map, mergeDeepRight, omit, path, union, append, without } from 'ramda';
import { isEmpty } from 'lodash';

/*
 Here is a quick view of what the chats state looks like:
    chats {
       1: {
         id: 1
         name: "chat name 1",
         users: [1,2,3],
         lastMessageRead: 10,
         messages: [1,4,6,8,10],
         type: DIRECT_MESSAGE,
         typingUsers: [1]
       },
       2: {
         id: 2
         name: "chat name 2",
         users: [1,2,3,4,5,6],
         lastMessageRead: 9,
         messages: [2,3,5,7,9],
         type: PUBLIC_CHANNEL,
         typingUsers: [1,2]
       },
    }

 */

const HAS_USER_MENTION_KEY = 'userMentioned';
const LAST_MESSAGE_READ_KEY = 'lastMessageRead';
const MESSAGES_KEY = 'messages';
const SCROLL_POSITION_KEY = 'scrollPosition';
const MESSAGE_INPUT_DATA_KEY = 'messageInputData';

const chats = (state = {}, action) => {
  switch (action.type) {
    case types.GET_ALL_CHATS_SUCCESS:
      if (action.payload && action.payload.chats) {
        // When a user has no chats, action.payload is {}
        return action.payload.chats;
      }
      return state;
    case types.GET_CHATS_SUCCESS:
      return mergeDeepRight(state, action.payload.chats);
    case types.GET_UNREAD_COUNT_SUCCESS: {
      const { chatId, count } = action.payload;
      const chat = state[chatId];
      return  {
        ...state,
        [chatId]: {
          ...chat,
          unreadCount: count,
        },
      };
    }
    case types.INCREMENT_UNREAD_COUNT: {
      const { chatId } = action.payload;
      const chat = state[chatId];
      return  {
        ...state,
        [chatId]: {
          ...chat,
          unreadCount: chat.unreadCount + 1,
        },
      };
    }
    case types.CLEAR_UNREAD_COUNT: {
      const { chatId } = action.payload;
      const chat = state[chatId];
      return  {
        ...state,
        [chatId]: {
          ...chat,
          unreadCount: 0,
        },
      };
    }
    case types.MARK_AS_READ_SUCCESS:
      const { chatId, messageId } = action.payload;
      // only update state if a chatId is in the payload
      if (chatId) {
        return assocPath([chatId, HAS_USER_MENTION_KEY], false, assocPath([chatId, LAST_MESSAGE_READ_KEY], messageId, state));
      }
      return state;
    case types.GET_MESSAGES_SUCCESS: {
      const { chatId, messages } = action.payload;
      const existingMessageIds = path([chatId, MESSAGES_KEY], state);
      const newMessageIds = map(id => parseInt(id, 10), keys(messages));

      // Add received messages to chat's existing message list
      return assocPath([chatId, MESSAGES_KEY], union(newMessageIds, existingMessageIds), state);
    }
    case types.ADD_LOCAL_MESSAGE: {
      const { chatId, message } = action.payload;
      const existingMessageIds = path([chatId, MESSAGES_KEY], state);

      // Add received message to chat's existing message list
      return assocPath([chatId, MESSAGES_KEY], [...existingMessageIds, message.id], state);
    }
    case types.MESSAGE_RECEIVED_SUCCESS: {
      const { chatId, messages } = action.payload;
      const existingMessageIds = path([chatId, MESSAGES_KEY], state);
      const newMessageIds = map(id => parseInt(id, 10), keys(messages));

      // Add received message to chat's existing message list
      return assocPath([chatId, MESSAGES_KEY], union(existingMessageIds, newMessageIds), state);
    }
    case types.REMOVE_MESSAGE: {
      const { chatId, id } = action.payload.message;
      const existingMessageIds = path([chatId, MESSAGES_KEY], state);
      return assocPath([chatId, MESSAGES_KEY], without(existingMessageIds, id), state);
    }
    case types.ADD_USERS_TO_CHANNEL_SUCCESS: {
      const { chatId, userIds } = action.payload;

      const chat = state[chatId];

      // When the chat is initially being created, this event is published before we have the chat in state.
      // To avoid an error, return the state as is and the users will be returned when the chat is fetched.
      if (isEmpty(chat)) {
        return state;
      }

      // Update the chat users with the newly invited users
      return {
        ...state,
        [chatId] : {
          ...chat,
          users: chat.users ? chat.users.concat(userIds) : userIds,
        },
      };
    }
    // You joined a channel. getChats will be called to update state separately
    case types.JOIN_CHANNEL_SUCCESS: {
      return state;
    }
    // You left a channel
    case types.LEAVE_CHANNEL_SUCCESS: {
      const { chatId } = action.payload;
      return omit([chatId], state);
    }
    // Someone left a channel you are in
    case types.USER_LEFT_CHANNEL: {
      const { userId, chatId } = action.payload;
      const chat = state[chatId];
      return {
        ...state,
        [chatId]: {
          ...chat,
          users: chat.users.filter(id => id !== userId),
        },
      };
    }
    case types.USER_MENTIONED: {
      const { chatId } = action.payload;
      if (chatId) {
        return assocPath([chatId, HAS_USER_MENTION_KEY], true, state);
      }
      return state;
    }
    case types.SHOW_CHAT_SUCCESS:
    case types.HIDE_CHAT_SUCCESS: {
      const { chatId, hidden } = action.payload;
      const chat = state[chatId];
      return {
        ...state,
        [chatId]: {
          ...chat,
          hidden,
        },
      };
    }
    case types.SAVE_SCROLL_POSITION: {
      const { chatId, scrollPosition } = action.payload;
      if (chatId) {
        return assocPath([chatId, SCROLL_POSITION_KEY], scrollPosition, state);
      }
      return state;
    }
    case types.TYPING_RECEIVED: {
      const { chatId, typistUserId } = action.payload;
      const chat = state[chatId];

      const typingUsers = append(typistUserId, chat.typingUsers);

      return {
        ...state,
        [chatId] : {
          ...chat,
          typingUsers,
        },
      };
    }
    case types.TYPING_COMPLETE: {
      const { chatId, typistUserId } = action.payload;
      const chat = state[chatId];

      const typingUsers = (chat && chat.typingUsers && chat.typingUsers.slice()) || [];
      const firstIndex = typingUsers.indexOf(typistUserId);

      if (firstIndex !== -1) {
        typingUsers.splice(firstIndex, 1);
      }

      return {
        ...state,
        [chatId] : {
          ...chat,
          typingUsers,
        },
      };
    }
    case types.SAVE_MESSAGE_INPUT_DATA: {
      const { chatId, messageInputData } = action.payload;
      if (chatId) {
        return assocPath([chatId, MESSAGE_INPUT_DATA_KEY], messageInputData, state);
      }
      return state;
    }
    default:
      return state;
  }
};

export default chats;
